import React, { useEffect, useMemo, useState } from "react";
import { Stepper, Select } from "@mantine/core";
//import "../../../site.css";
import "./FormPractice.css";
import { useLanguage } from "../../../context/LanguageContext";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billlede-1.png";
import { useParams } from "react-router-dom";

interface FormPracticeDto {
    practiceId?: number;
    country?: string;
    practiceDate?: string;
    practiceTime?: number;
    practiceLocation?: number;
    numberOfCourts?: number;
    numberOfPlayers?: number;
    numberOfMinuts?: number;
    createdDate?: string;
    eventId?: number;
    userId?: number;
}

interface OptionDto {
    value: number;
    text: string;
}

interface PracticeDateDto {
    value: number;
    date: string;
    eventId: number;
}
interface PracticePlanDto {
    practicePlanId: number;
    eventId: number;
    practiceDate: string;
    status?: string;
}

const FormPractice: React.FC = () => {
    const { language } = useLanguage();
    const { eventId } = useParams<{ eventId: string }>();

    const [practiceDates, setPracticeDates] =
        useState<PracticeDateDto[]>([]);

    const userId = sessionStorage.getItem("userId");

    const eventIdNumber = Number(eventId);
    const userIdNumber = Number(userId);

    const t = useMemo(() => {
        const da = language === "da";

        return {
            heroTitleEn: "Book practice",
            heroTitleDa: "Book træning",
            heroDescEn: "Here you can request practice courts",
            heroDescDa: "Her kan du anmode om træningsbaner",

            stepDate: da ? "Dato" : "Date",
            stepCapacity: da ? "Kapacitet" : "Capacity",
            stepConfirm: da ? "Bekræft" : "Confirm",

            cardTitleCreate: da ? "Book træning" : "Book practice",
            cardTitleEdit: da ? "Ret træningsbooking" : "Edit practice booking",

            lblCountry: da ? "Land" : "Country",
            lblPracticeDate: da ? "Træningsdato" : "Practice date",
            lblPracticeTime: da ? "Træningstid" : "Practice time",
            lblCourts: da ? "Baner" : "Courts",
            lblPlayers: da ? "Spillere" : "Players",
            lblMinutes: da ? "Minutter" : "Minutes",

            tableTitle: da
                ? "Mine træningsbookinger"
                : "My practice bookings",

            tableCountry: da ? "Land" : "Country",
            tableDate: da ? "Dato" : "Date",
            tableTime: da ? "Tid" : "Time",
            tableCourts: da ? "Baner" : "Courts",
            tablePlayers: da ? "Spillere" : "Players",
            tableMinutes: da ? "Minutter" : "Minutes",
            tableAction: da ? "Handling" : "Action",

            edit: da ? "Ret" : "Edit",
            back: da ? "Tilbage" : "Back",
            next: da ? "Næste" : "Next",
            save: da ? "Gem" : "Save",
            create: da ? "Opret" : "Create",
            newBooking: da ? "Ny booking" : "New booking",

            noBookings: da
                ? "Ingen træningsbookinger fundet"
                : "No practice bookings found",

            savedCreate: da
                ? "Træningsbooking gemt ✅"
                : "Practice booking saved ✅",

            savedUpdate: da
                ? "Træningsbooking opdateret ✅"
                : "Practice booking updated ✅",

            fetchError: da
                ? "Kunne ikke hente træningsbookinger."
                : "Failed to fetch practices.",

            saveError: da
                ? "Kunne ikke gemme træningsbooking."
                : "Error saving practice booking.",

            optionsError: da
                ? "Kunne ikke hente træningsindstillinger."
                : "Failed to fetch practice settings.",

            lblLocation: da ? "Træningssted" : "Practice location",
            tableLocation: da ? "Sted" : "Location",

            dash: "—",
        };
    }, [language]);

    const API_URL =
        `${process.env.REACT_APP_API_URL}/api/FormPractice`;

    const PRACTICE_TIME_URL =
        `${process.env.REACT_APP_API_URL}/api/practice-planner/times`;

    const PRACTICE_MINUTS_URL =
        `${process.env.REACT_APP_API_URL}/api/practice-planner/minutes`;

    const PRACTICE_LOCATION_URL =
        `${process.env.REACT_APP_API_URL}/api/practice-planner/locations`;

    const PRACTICE_DATE_URL =
        `${process.env.REACT_APP_API_URL}/api/practice-planner/dates`;

    const PRACTICE_PLAN_URL =
        `${process.env.REACT_APP_API_URL}/api/practice-planner/event`;

    const [active, setActive] = useState(0);

    const [practices, setPractices] =
        useState<FormPracticeDto[]>([]);

    const [practiceTimes, setPracticeTimes] =
        useState<OptionDto[]>([]);

    const [practiceMinutesOptions, setPracticeMinutesOptions] =
        useState<OptionDto[]>([]);

    const [formData, setFormData] =
        useState<FormPracticeDto>({});

    const [editingId, setEditingId] =
        useState<number | null>(null);

    const [savedMessage, setSavedMessage] =
        useState("");

    const [errorMessage, setErrorMessage] =
        useState("");

    const [practiceLocations, setPracticeLocations] =
        useState<OptionDto[]>([]);

    /*
  * Only the first two upcoming Practice Planner
  * dates are available for booking.
  *
  * Once the first date has passed, the next
  * configured date becomes available.
  */
    const availableBookingDates = useMemo(() => {
        const today = new Date();

        const todayString =
            `${today.getFullYear()}-${String(
                today.getMonth() + 1
            ).padStart(2, "0")}-${String(
                today.getDate()
            ).padStart(2, "0")}`;

        const dates = practiceDates
            .map((item) => {
                if (!item.date) {
                    return null;
                }

                return item.date.includes("T")
                    ? item.date.split("T")[0]
                    : item.date;
            })
            .filter(
                (date): date is string => !!date
            )
            .filter(
                (date, index, dates) =>
                    dates.indexOf(date) === index
            )
            .sort();

        /*
         * Remove dates that have already passed.
         */
        const upcomingDates = dates.filter(
            (date) => date >= todayString
        );

        /*
         * Only allow the first two upcoming dates.
         */
        return upcomingDates.slice(0, 2);
    }, [practiceDates]);

    /*
 * Main Hall is only available on Mondays.
 */
    const availablePracticeLocations =
        useMemo(() => {
            if (!formData.practiceDate) {
                return practiceLocations;
            }

            const selectedDate = new Date(
                `${formData.practiceDate}T12:00:00`
            );

            const isMonday =
                selectedDate.getDay() === 1;

            if (isMonday) {
                return practiceLocations;
            }

            return practiceLocations.filter(
                (location) =>
                    location.text.toLowerCase() !==
                    "main hall"
            );
        }, [
            practiceLocations,
            formData.practiceDate,
        ]);


    useEffect(() => {
        if (!eventIdNumber) {
            return;
        }

        fetchPracticeTimes();
        fetchPracticeMinutes();
        fetchPracticeLocations();
        fetchPracticeDates();
    }, [eventIdNumber]);

    useEffect(() => {
        if (
            eventIdNumber &&
            userIdNumber
        ) {
            fetchPractices();
        }
    }, [
        eventIdNumber,
        userIdNumber,
    ]);

    const getPracticeLocationText = (
        value?: number
    ) =>
        practiceLocations.find(
            (x) => x.value === value
        )?.text || t.dash;

    /*
     * GET TIMES FROM PRACTICE PLANNER
     *
     * GET /api/practice-planner/times/{eventId}
     */
    const fetchPracticeTimes = async () => {
        if (!eventIdNumber) {
            return;
        }

        try {
            const res = await fetch(
                `${PRACTICE_TIME_URL}/${eventIdNumber}`
            );

            if (!res.ok) {
                setErrorMessage(
                    t.optionsError
                );
                return;
            }

            const data =
                await res.json();

            setPracticeTimes(data);
        } catch {
            setErrorMessage(
                t.optionsError
            );
        }
    };

    /*
     * GET MINUTES FROM PRACTICE PLANNER
     *
     * GET /api/practice-planner/minutes/{eventId}
     */
    const fetchPracticeMinutes = async () => {
        if (!eventIdNumber) {
            return;
        }

        try {
            const res = await fetch(
                `${PRACTICE_MINUTS_URL}/${eventIdNumber}`
            );

            if (!res.ok) {
                setErrorMessage(
                    t.optionsError
                );
                return;
            }

            const data =
                await res.json();

            setPracticeMinutesOptions(
                data
            );
        } catch {
            setErrorMessage(
                t.optionsError
            );
        }
    };

    /*
     * GET LOCATIONS FROM PRACTICE PLANNER
     *
     * GET /api/practice-planner/locations/{eventId}
     */
    const fetchPracticeLocations = async () => {
        if (!eventIdNumber) {
            return;
        }

        try {
            const res = await fetch(
                `${PRACTICE_LOCATION_URL}/${eventIdNumber}`
            );

            if (!res.ok) {
                setErrorMessage(
                    t.optionsError
                );
                return;
            }

            const data =
                await res.json();

            setPracticeLocations(
                data
            );
        } catch {
            setErrorMessage(
                t.optionsError
            );
        }
    };

    /*
 * GET DATES FROM PRACTICE PLANNER
 *
 * GET /api/practice-planner/dates/{eventId}
 */
    const fetchPracticeDates = async () => {
        if (!eventIdNumber) {
            return;
        }

        try {
            const res = await fetch(
                `${PRACTICE_DATE_URL}/${eventIdNumber}`
            );

            if (!res.ok) {
                setErrorMessage(
                    t.optionsError
                );
                return;
            }

            const data =
                await res.json();

            setPracticeDates(
                Array.isArray(data)
                    ? data
                    : []
            );
        } catch {
            setErrorMessage(
                t.optionsError
            );
        }
    };

    const fetchPractices = async () => {
        if (
            !eventIdNumber ||
            !userIdNumber
        ) {
            setErrorMessage(
                "Event eller bruger mangler."
            );

            return;
        }

        const res = await fetch(
            `${API_URL}?userId=${userIdNumber}&eventId=${eventIdNumber}`
        );

        if (!res.ok) {
            setErrorMessage(
                t.fetchError
            );

            return;
        }

        const data =
            await res.json();

        setPractices(data);
    };

    const getPracticeTimeText = (
        value?: number
    ) =>
        practiceTimes.find(
            (x) => x.value === value
        )?.text || t.dash;

    const getPracticeMinutesText = (
        value?: number
    ) =>
        practiceMinutesOptions.find(
            (x) => x.value === value
        )?.text || t.dash;

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const {
            name,
            value,
            type,
        } = e.target;

        setFormData((prev) => ({
            ...prev,

            [name]:
                type === "number"
                    ? value === ""
                        ? undefined
                        : Number(value)
                    : value,
        }));
    };

    const resetForm = () => {
        setFormData({});
        setEditingId(null);
        setActive(0);
        setSavedMessage("");
        setErrorMessage("");
    };

    const handleEdit = (
        practice: FormPracticeDto
    ) => {
        setEditingId(
            practice.practiceId ?? null
        );

        setSavedMessage("");
        setErrorMessage("");

        setFormData({
            ...practice,

            practiceDate:
                practice.practiceDate?.includes("T")
                    ? practice.practiceDate.split("T")[0]
                    : practice.practiceDate,
        });

        setActive(0);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const isStepValid = (
        step: number
    ) => {
        switch (step) {
            case 0:
                return (
                    !!formData.country &&
                    !!formData.practiceDate &&
                    !!formData.practiceTime &&
                    !!formData.practiceLocation &&
                    availableBookingDates.includes(
                        formData.practiceDate
                    )
                );

            case 1:
                return true;

            case 2:
                return true;

            default:
                return true;
        }
    };

    const nextStep = () => {
        if (!isStepValid(active)) {
            return;
        }

        setActive(
            (prev) => prev + 1
        );
    };

    const prevStep = () => {
        setActive(
            (prev) => prev - 1
        );
    };

    const handleSubmit = async () => {
        if (!isStepValid(0)) {
            setActive(0);
            return;
        }

        setErrorMessage("");

        const payload: FormPracticeDto = {
            ...formData,

            practiceId:
                editingId ??
                formData.practiceId,

            eventId:
                eventIdNumber,

            userId:
                userIdNumber,
        };

        const isEditing =
            !!editingId;

        const url = isEditing
            ? `${API_URL}/${editingId}?userId=${userIdNumber}&eventId=${eventIdNumber}`
            : `${API_URL}?userId=${userIdNumber}&eventId=${eventIdNumber}`;

        const method =
            isEditing
                ? "PUT"
                : "POST";

        const res = await fetch(
            url,
            {
                method,

                headers: {
                    "Content-Type":
                        "application/json",
                },

                body:
                    JSON.stringify(
                        payload
                    ),
            }
        );

        if (!res.ok) {
            setErrorMessage(
                t.saveError
            );

            return;
        }

        await fetchPractices();

        setSavedMessage(
            isEditing
                ? t.savedUpdate
                : t.savedCreate
        );

        setActive(3);
    };

    return (
        <div>
            <HeroSectionV2
                titleEn={t.heroTitleEn}
                titleDa={t.heroTitleDa}
                descriptionEn={
                    t.heroDescEn
                }
                descriptionDa={
                    t.heroDescDa
                }
                imageSrc={Billede1}
            />

            <div
                className="form-practice-card"
                style={{
                    maxWidth: 900,
                    margin:
                        "40px auto",
                }}
            >
                <button className="form-practice-card-button">
                    {editingId
                        ? t.cardTitleEdit
                        : t.cardTitleCreate}
                </button>

                <div className="form-practice-card-content">

                    {errorMessage && (
                        <p
                            style={{
                                color:
                                    "#b00020",
                                marginBottom:
                                    12,
                            }}
                        >
                            {errorMessage}
                        </p>
                    )}

                    <Stepper
                        active={active}
                        onStepClick={
                            setActive
                        }
                        allowNextStepsSelect={
                            false
                        }
                        color="#e63946"
                        styles={{
                            stepIcon: {
                                borderRadius:
                                    "50%",
                                borderColor:
                                    "#e63946",
                                color:
                                    "#e63946",
                                width: 36,
                                height: 36,
                            },

                            stepCompletedIcon: {
                                borderRadius:
                                    "50%",
                                backgroundColor:
                                    "#e63946",
                                borderColor:
                                    "#e63946",
                                width: 36,
                                height: 36,
                            },

                            separator: {
                                borderColor:
                                    "#e63946",
                            },
                        }}
                    >

                        {/* STEP 1 */}

                        <Stepper.Step
                            label={
                                t.stepDate
                            }
                        >
                            <label>
                                {t.lblCountry} *
                            </label>

                            <input
                                name="country"
                                value={
                                    formData.country ||
                                    ""
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <label>
                                {
                                    t.lblPracticeDate
                                } *
                            </label>

                            <Select
                                placeholder={
                                    language ===
                                        "da"
                                        ? "Vælg træningsdato"
                                        : "Select practice date"
                                }

                                value={
                                    formData.practiceDate ||
                                    null
                                }

                                onChange={(
                                    val
                                ) =>
                                    setFormData(
                                        (
                                            prev
                                        ) => ({
                                            ...prev,

                                            practiceDate:
                                                val ||
                                                undefined,

                                            practiceLocation:
                                                undefined,
                                        })
                                    )
                                }

                                data={availableBookingDates.map(
                                    (
                                        date
                                    ) => ({
                                        value:
                                            date,

                                        label:
                                            new Date(
                                                `${date}T12:00:00`
                                            ).toLocaleDateString(
                                                language ===
                                                    "da"
                                                    ? "da-DK"
                                                    : "en-GB",
                                                {
                                                    weekday:
                                                        "long",
                                                    day:
                                                        "2-digit",
                                                    month:
                                                        "2-digit",
                                                }
                                            ),
                                    })
                                )}
                            />

                            <label>
                                {
                                    t.lblPracticeTime
                                } *
                            </label>

                            <Select
                                placeholder={
                                    language ===
                                        "da"
                                        ? "Vælg træningstid"
                                        : "Select practice time"
                                }

                                value={
                                    formData.practiceTime !==
                                        undefined
                                        ? String(
                                            formData.practiceTime
                                        )
                                        : null
                                }

                                onChange={(
                                    val
                                ) =>
                                    setFormData(
                                        (
                                            prev
                                        ) => ({
                                            ...prev,

                                            practiceTime:
                                                val
                                                    ? Number(
                                                        val
                                                    )
                                                    : undefined,
                                        })
                                    )
                                }

                                data={practiceTimes.map(
                                    (
                                        x
                                    ) => ({
                                        value:
                                            String(
                                                x.value
                                            ),

                                        label:
                                            x.text,
                                    })
                                )}
                            />

                            <label>
                                {
                                    t.lblLocation
                                } *
                            </label>

                            <Select
                                placeholder={
                                    !formData.practiceDate
                                        ? language ===
                                            "da"
                                            ? "Vælg først en dato"
                                            : "Select a date first"
                                        : language ===
                                            "da"
                                            ? "Vælg træningssted"
                                            : "Select practice location"
                                }

                                disabled={
                                    !formData.practiceDate
                                }

                                clearable

                                value={
                                    formData.practiceLocation !==
                                        undefined
                                        ? String(
                                            formData.practiceLocation
                                        )
                                        : null
                                }

                                onChange={(
                                    val
                                ) =>
                                    setFormData(
                                        (
                                            prev
                                        ) => ({
                                            ...prev,

                                            practiceLocation:
                                                val
                                                    ? Number(
                                                        val
                                                    )
                                                    : undefined,
                                        })
                                    )
                                }

                                data={availablePracticeLocations.map(
                                    (
                                        x
                                    ) => ({
                                        value:
                                            String(
                                                x.value
                                            ),

                                        label:
                                            x.text,
                                    })
                                )}
                            />

                        </Stepper.Step>

                        {/* STEP 2 */}

                        <Stepper.Step
                            label={
                                t.stepCapacity
                            }
                        >
                            <label>
                                {t.lblCourts}
                            </label>

                            <input
                                type="number"
                                name="numberOfCourts"
                                value={
                                    formData.numberOfCourts ??
                                    ""
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <label>
                                {t.lblPlayers}
                            </label>

                            <input
                                type="number"
                                name="numberOfPlayers"
                                value={
                                    formData.numberOfPlayers ??
                                    ""
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <label>
                                {t.lblMinutes}
                            </label>

                            <Select
                                placeholder={
                                    language ===
                                        "da"
                                        ? "Vælg antal minutter"
                                        : "Select minutes"
                                }

                                value={
                                    formData.numberOfMinuts !==
                                        undefined
                                        ? String(
                                            formData.numberOfMinuts
                                        )
                                        : null
                                }

                                onChange={(
                                    val
                                ) =>
                                    setFormData(
                                        (
                                            prev
                                        ) => ({
                                            ...prev,

                                            numberOfMinuts:
                                                val
                                                    ? Number(
                                                        val
                                                    )
                                                    : undefined,
                                        })
                                    )
                                }

                                data={practiceMinutesOptions.map(
                                    (
                                        x
                                    ) => ({
                                        value:
                                            String(
                                                x.value
                                            ),

                                        label:
                                            x.text,
                                    })
                                )}
                            />

                        </Stepper.Step>

                        {/* STEP 3 */}

                        <Stepper.Step
                            label={
                                t.stepConfirm
                            }
                        >
                            <p>
                                <b>
                                    {
                                        t.lblCountry
                                    }:
                                </b>{" "}
                                {
                                    formData.country ||
                                    t.dash
                                }
                            </p>

                            <p>
                                <b>
                                    {
                                        t.lblPracticeDate
                                    }:
                                </b>{" "}
                                {
                                    formData.practiceDate ||
                                    t.dash
                                }
                            </p>

                            <p>
                                <b>
                                    {
                                        t.lblPracticeTime
                                    }:
                                </b>{" "}
                                {getPracticeTimeText(
                                    formData.practiceTime
                                )}
                            </p>

                            <p>
                                <b>
                                    {
                                        t.lblLocation
                                    }:
                                </b>{" "}
                                {getPracticeLocationText(
                                    formData.practiceLocation
                                )}
                            </p>

                            <hr />

                            <p>
                                <b>
                                    {
                                        t.lblCourts
                                    }:
                                </b>{" "}
                                {
                                    formData.numberOfCourts ??
                                    t.dash
                                }
                            </p>

                            <p>
                                <b>
                                    {
                                        t.lblPlayers
                                    }:
                                </b>{" "}
                                {
                                    formData.numberOfPlayers ??
                                    t.dash
                                }
                            </p>

                            <p>
                                <b>
                                    {
                                        t.lblMinutes
                                    }:
                                </b>{" "}
                                {getPracticeMinutesText(
                                    formData.numberOfMinuts
                                )}
                            </p>

                        </Stepper.Step>

                        <Stepper.Completed>
                            <p>
                                {
                                    savedMessage ||
                                    (
                                        editingId
                                            ? t.savedUpdate
                                            : t.savedCreate
                                    )
                                }
                            </p>

                            <button
                                style={{
                                    marginTop:
                                        10,
                                }}
                                onClick={
                                    resetForm
                                }
                            >
                                {
                                    t.newBooking
                                }
                            </button>
                        </Stepper.Completed>

                    </Stepper>

                    {active < 3 && (
                        <div
                            style={{
                                marginTop:
                                    20,
                            }}
                        >
                            {active > 0 && (
                                <button
                                    onClick={
                                        prevStep
                                    }
                                >
                                    {t.back}
                                </button>
                            )}

                            {active === 2 ? (
                                <button
                                    style={{
                                        marginLeft:
                                            10,
                                    }}
                                    onClick={
                                        handleSubmit
                                    }
                                >
                                    {editingId
                                        ? t.save
                                        : t.create}
                                </button>
                            ) : (
                                <button
                                    style={{
                                        marginLeft:
                                            10,
                                    }}
                                    onClick={
                                        nextStep
                                    }
                                    disabled={
                                        !isStepValid(
                                            active
                                        )
                                    }
                                >
                                    {t.next}
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* EXISTING BOOKINGS */}

            <div
                className="form-practice-card"
                style={{
                    maxWidth: 900,
                    margin:
                        "40px auto",
                }}
            >
                <button className="form-practice-card-button">
                    {
                        t.tableTitle
                    }
                </button>

                <div className="form-practice-card-content">

                    {practices.length === 0 ? (
                        <p>
                            {
                                t.noBookings
                            }
                        </p>
                    ) : (
                        <div
                            style={{
                                overflowX:
                                    "auto",
                            }}
                        >
                            <table
                                style={{
                                    width:
                                        "100%",
                                    borderCollapse:
                                        "collapse",
                                }}
                            >
                                <thead>
                                    <tr>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableCountry
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableDate
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableTime
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableCourts
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tablePlayers
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableMinutes
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableLocation
                                            }
                                        </th>

                                        <th
                                            style={{
                                                textAlign:
                                                    "left",
                                                padding:
                                                    "10px",
                                                borderBottom:
                                                    "1px solid #ddd",
                                            }}
                                        >
                                            {
                                                t.tableAction
                                            }
                                        </th>

                                    </tr>
                                </thead>

                                <tbody>

                                    {practices.map(
                                        (
                                            practice
                                        ) => (
                                            <tr
                                                key={
                                                    practice.practiceId
                                                }
                                            >

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {
                                                        practice.country ||
                                                        t.dash
                                                    }
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {
                                                        practice.practiceDate?.includes(
                                                            "T"
                                                        )
                                                            ? practice.practiceDate.split(
                                                                "T"
                                                            )[0]
                                                            : practice.practiceDate ||
                                                            t.dash
                                                    }
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {getPracticeTimeText(
                                                        practice.practiceTime
                                                    )}
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {
                                                        practice.numberOfCourts ??
                                                        t.dash
                                                    }
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {
                                                        practice.numberOfPlayers ??
                                                        t.dash
                                                    }
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {getPracticeMinutesText(
                                                        practice.numberOfMinuts
                                                    )}
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    {getPracticeLocationText(
                                                        practice.practiceLocation
                                                    )}
                                                </td>

                                                <td
                                                    style={{
                                                        padding:
                                                            "10px",
                                                        borderBottom:
                                                            "1px solid #eee",
                                                    }}
                                                >
                                                    <button
                                                        onClick={() =>
                                                            handleEdit(
                                                                practice
                                                            )
                                                        }
                                                    >
                                                        {
                                                            t.edit
                                                        }
                                                    </button>
                                                </td>

                                            </tr>
                                        )
                                    )}

                                </tbody>
                            </table>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default FormPractice;